import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Button,
  Input,
  Label,
  LastUpdated,
  Badge,
  Icon,
} from '@repo/ui';
import type {
  ConfigModalProps,
  QboFormData,
  QboWebhookSubscription,
} from '@xtnd/web/shared/types/tenantSettingTypes';
import { getDefaultQboWebhookSubscriptions } from '@xtnd/web/features/webhooks/constants';
import { qboIntegrationApi } from '@xtnd/web/features/settings/api/qboIntegrationApi';
import { showToast } from '@repo/ui';
import { useQboConfig, useQboOAuthUrlMutation, useQboTestConnection } from '@xtnd/web/features/settings/queries';

export const QBOConfigModal: React.FC<ConfigModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
}) => {
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<QboFormData>({
    appId: '',
    clientId: '',
    clientSecret: '',
    webhookSecret: '',
    realmId: '',
    enableWebhooks: false,
    webhookSubscriptions: getDefaultQboWebhookSubscriptions(),
  });
  const [errors, setErrors] = useState<Partial<QboFormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // React Query for fetching QBO config
  const {
    data: qboConfig,
    isLoading: isLoadingConfig,
    refetch: refetchConfig,
  } = useQboConfig(isOpen);

  // Mutation for OAuth URL generation
  const oauthUrlMutation = useQboOAuthUrlMutation();

  // Handle OAuth success with custom logic
  const handleOAuthSuccess = (response: any, variables: any) => {
    // Store credentials temporarily for the callback
    sessionStorage.setItem(
      'qbo-oauth-config',
      JSON.stringify({
        appId: formData.appId,
        clientId: variables.clientId,
        clientSecret: variables.clientSecret,
        state: response.state,
      })
    );

    // Open OAuth window
    const popup = window.open(
      response.authUrl,
      'qbo-oauth',
      'width=600,height=700,scrollbars=yes,resizable=yes'
    );

    if (popup) {
      showToast.info('Please complete authorization in the popup window...');
    } else {
      showToast.error('Popup blocked. Please allow popups and try again.');
    }
  };

  // Mutation for connection testing
  const testConnectionMutation = useQboTestConnection();

  // Helper function to merge existing webhook subscriptions with all available models
  const mergeWebhookSubscriptions = (existingSubscriptions?: QboWebhookSubscription[]) => {
    const defaultSubscriptions = getDefaultQboWebhookSubscriptions();

    if (!existingSubscriptions || existingSubscriptions.length === 0) {
      return defaultSubscriptions;
    }

    // Create a map of existing subscriptions by modelName
    const existingMap = new Map(existingSubscriptions.map(sub => [sub.modelName, sub]));

    // Merge: use existing config if available, otherwise use default
    return defaultSubscriptions.map(
      defaultSub => existingMap.get(defaultSub.modelName) || defaultSub
    );
  };

  // Initialize form when modal opens or data changes
  useEffect(() => {
    if (isOpen && qboConfig) {
      setFormData({
        appId: qboConfig.appId || '',
        clientId: qboConfig.clientId || '',
        clientSecret: '', // Never pre-fill secrets
        webhookSecret: '', // Never pre-fill secrets
        realmId: qboConfig.realmId || '',
        enableWebhooks: initialData?.webhooksConfigured || false,
        webhookSubscriptions: mergeWebhookSubscriptions(initialData?.webhookSubscriptions),
      });
    } else if (isOpen && !qboConfig && !isLoadingConfig) {
      // Fallback to empty values if no config found
      setFormData({
        appId: '',
        clientId: '',
        clientSecret: '',
        webhookSecret: '',
        realmId: '',
        enableWebhooks: initialData?.webhooksConfigured || false,
        webhookSubscriptions: mergeWebhookSubscriptions(initialData?.webhookSubscriptions),
      });
    }
  }, [isOpen, qboConfig, isLoadingConfig, initialData]);

  const validateForm = (): boolean => {
    const newErrors: Partial<QboFormData> = {};

    if (!formData.clientId.trim()) {
      newErrors.clientId = 'Client ID is required';
    }

    // Only require client secret if it's not set yet
    const isClientSecretSet = initialData?.clientSecret?.ssmPath;
    if (!isClientSecretSet && !formData.clientSecret.trim()) {
      newErrors.clientSecret = 'Client Secret is required';
    }

    // Realm ID is required for QBO
    if (!formData.realmId.trim()) {
      newErrors.realmId = 'Company ID (Realm ID) is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        appId: '',
        clientId: '',
        clientSecret: '',
        webhookSecret: '',
        realmId: '',
        enableWebhooks: false,
        webhookSubscriptions: mergeWebhookSubscriptions(),
      });
      setErrors({});
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Only include sensitive fields if they were actually entered
      const { clientSecret, webhookSecret, ...baseData } = formData;
      const dataToSave = {
        ...baseData,
        // Only include secrets if they have values
        ...(clientSecret.trim() && { clientSecret }),
        ...(webhookSecret?.trim() && { webhookSecret }),
      };

      await onSave(dataToSave as QboFormData);
      handleClose();
    } catch {
      // Silent error handling - error already shown by mutation
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof QboFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Generate OAuth authorization URL and open popup
  const handleAuthorizeApp = async () => {
    if (!formData.clientId.trim() || !formData.clientSecret.trim()) {
      showToast.error('Please enter Client ID and Client Secret first');
      return;
    }

    const redirectUri = `${window.location.origin}/qbo-redirect`;

    oauthUrlMutation.mutate(
      {
        clientId: formData.clientId,
        clientSecret: formData.clientSecret,
        redirectUri,
      },
      {
        onSuccess: (response) => handleOAuthSuccess(response, {
          clientId: formData.clientId,
          clientSecret: formData.clientSecret,
        }),
      }
    );
  };

  // OAuth callback handler
  useEffect(() => {
    if (!isOpen) return;

    const handleMessage = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      if (event.data.type === 'QBO_AUTH_SUCCESS') {
        try {
          const { code, state, realmId } = event.data;

          console.log('QBO_AUTH_SUCCESS', event.data);
          showToast.info('Processing authorization...');

          // Verify state matches
          const storedConfig = JSON.parse(sessionStorage.getItem('qbo-oauth-config') || '{}');
          if (state !== storedConfig.state) {
            throw new Error('Invalid authorization state');
          }

          // Exchange code for tokens
          await qboIntegrationApi.exchangeOAuthCode({
            code,
            state,
            realmId,
            clientId: storedConfig.clientId,
            clientSecret: storedConfig.clientSecret,
          });

          // Update form data with the realm ID
          setFormData(prev => ({
            ...prev,
            realmId: realmId,
            accessToken: '[CONNECTED]',
            refreshToken: '[CONNECTED]',
          }));

          // Refetch config to get updated status
          await refetchConfig();

          // Invalidate queries to refresh Settings page
          queryClient.invalidateQueries({ queryKey: ['tenant-config'] });
          queryClient.invalidateQueries({ queryKey: ['configuration-status'] });

          showToast.success('QuickBooks Online authorization successful!');
        } catch (error) {
          console.error('OAuth callback failed:', error);
          showToast.error(
            'Failed to complete authorization: ' +
              (error instanceof Error ? error.message : 'Unknown error')
          );
        } finally {
          sessionStorage.removeItem('qbo-oauth-config');
        }
      } else if (event.data.type === 'QBO_AUTH_ERROR') {
        showToast.error('Authorization failed: ' + (event.data.error || 'Unknown error'));
        sessionStorage.removeItem('qbo-oauth-config');
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [isOpen, refetchConfig, queryClient]);

  // Test connection using stored tokens
  const handleTestConnection = () => {
    testConnectionMutation.mutate();
  };

  const isClientSecretSet = initialData?.clientSecret?.ssmPath;

  return (
    <Dialog open={isOpen} onClose={handleClose} size="lg">
      <div className="max-h-[90vh] flex flex-col">
        <form onSubmit={handleSubmit} className="flex flex-col min-h-0">
          <DialogHeader>
            <div className="flex items-center justify-between w-full pr-8">
              <div>
                <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white">
                  Configure QuickBooks Online Integration
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-300 mtAuthorize XTND App-1">
                  Set up your QuickBooks Online API credentials
                </p>
              </div>
              {initialData?.updatedAt && <LastUpdated timestamp={initialData.updatedAt} />}
            </div>
          </DialogHeader>

          <DialogBody className="space-y-6 flex-1 overflow-y-auto pr-2 -mr-2 min-h-0">
            {/* Loading State */}
            {isLoadingConfig && (
              <div className="flex items-center justify-center py-8">
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-xtnd-blue"></div>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Loading configuration...
                  </span>
                </div>
              </div>
            )}

            {/* Current Authorization Status */}
            {!isLoadingConfig && qboConfig && (
              <div
                className={`rounded-lg p-4 border ${
                  qboConfig.configured
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  {qboConfig.configured ? (
                    <Icon
                      name="HiCheckCircle"
                      className="h-5 w-5 text-green-600 dark:text-green-400"
                    />
                  ) : (
                    <Icon name="HiXCircle" className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                  )}
                  <h3
                    className={`text-sm font-medium ${
                      qboConfig.configured
                        ? 'text-green-800 dark:text-green-200'
                        : 'text-gray-600 dark:text-gray-300'
                    }`}
                  >
                    {qboConfig.configured ? 'Authorization Complete' : 'Authorization Required'}
                  </h3>
                </div>
                <div className="text-xs space-y-1 text-gray-600 dark:text-gray-400">
                  <div className="flex items-center gap-2">
                    <span>Access Token:</span>
                    <Badge variant={qboConfig.hasAccessToken ? 'success' : 'secondary'}>
                      {qboConfig.hasAccessToken ? 'Available' : 'Missing'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>Refresh Token:</span>
                    <Badge variant={qboConfig.hasRefreshToken ? 'success' : 'secondary'}>
                      {qboConfig.hasRefreshToken ? 'Available' : 'Missing'}
                    </Badge>
                  </div>
                  {qboConfig.realmId && (
                    <div className="text-xs mt-1">Company ID: {qboConfig.realmId}</div>
                  )}
                  {qboConfig.tokenExpiresAt && (
                    <div className="text-xs mt-1">
                      Token expires: {new Date(qboConfig.tokenExpiresAt).toLocaleString()}
                    </div>
                  )}
                </div>
                {qboConfig.configured && (
                  <div className="mt-3 pt-3 border-t border-green-200 dark:border-green-800">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleTestConnection}
                      loading={testConnectionMutation.isPending}
                      disabled={testConnectionMutation.isPending}
                      className="text-green-700 dark:text-green-300 border-green-300 dark:border-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
                    >
                      {testConnectionMutation.isPending ? 'Testing...' : 'Test Connection'}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* API Credentials */}
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label
                  htmlFor="appId"
                  className="text-sm font-medium text-xtnd-dark dark:text-xtnd-white"
                >
                  App ID
                </Label>
                <Input
                  id="appId"
                  value={formData.appId}
                  onChange={e => handleInputChange('appId', e.target.value)}
                  placeholder="Enter QuickBooks Online Application ID"
                  error={errors.appId}
                  disabled={isSubmitting}
                  fullWidth
                />
                <p className="text-xs text-gray-500 mt-1">
                  QuickBooks Online Application ID from your registered app
                </p>
              </div>

              <div>
                <Label
                  htmlFor="clientId"
                  className="text-sm font-medium text-xtnd-dark dark:text-xtnd-white"
                >
                  Client ID *
                </Label>
                <Input
                  id="clientId"
                  value={formData.clientId}
                  onChange={e => handleInputChange('clientId', e.target.value)}
                  placeholder="Enter QuickBooks Online Client ID"
                  error={errors.clientId}
                  disabled={isSubmitting}
                  fullWidth
                  required
                />
              </div>

              <div>
                <Label
                  htmlFor="clientSecret"
                  className="text-sm font-medium text-xtnd-dark dark:text-xtnd-white"
                >
                  Client Secret {!isClientSecretSet && '*'}
                </Label>
                <Input
                  id="clientSecret"
                  type="password"
                  value={formData.clientSecret}
                  onChange={e => handleInputChange('clientSecret', e.target.value)}
                  placeholder={
                    isClientSecretSet
                      ? '••••••••••••••••••••'
                      : 'Enter QuickBooks Online Client Secret'
                  }
                  error={errors.clientSecret}
                  disabled={isSubmitting}
                  fullWidth
                  required={!isClientSecretSet}
                />
                {isClientSecretSet && (
                  <p className="text-xs text-gray-500 mt-1">
                    Leave blank to keep current value, or enter new value to update.
                  </p>
                )}
              </div>

              <div>
                <Label
                  htmlFor="webhookSecret"
                  className="text-sm font-medium text-xtnd-dark dark:text-xtnd-white"
                >
                  Webhook Secret
                </Label>
                <Input
                  id="webhookSecret"
                  type="password"
                  value={formData.webhookSecret || ''}
                  onChange={e => handleInputChange('webhookSecret', e.target.value)}
                  placeholder={
                    initialData?.webhookSecret?.ssmPath
                      ? '••••••••••••••••••••'
                      : 'Enter webhook secret for signature verification'
                  }
                  error={errors.webhookSecret}
                  disabled={isSubmitting}
                  fullWidth
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Secret used to verify webhook signatures from QuickBooks Online.{' '}
                  {initialData?.webhookSecret?.ssmPath && 'Leave blank to keep current value.'}
                </p>
              </div>

              <div>
                <Label
                  htmlFor="realmId"
                  className="text-sm font-medium text-xtnd-dark dark:text-xtnd-white"
                >
                  Company ID (Realm ID) *
                </Label>
                <Input
                  id="realmId"
                  value={formData.realmId}
                  onChange={e => handleInputChange('realmId', e.target.value)}
                  placeholder="Enter QuickBooks Online Company ID"
                  error={errors.realmId}
                  disabled={isSubmitting}
                  fullWidth
                  required
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  This will be auto-filled when you authorize the app.
                </p>
              </div>

              {/* Authorize XTND App Button */}
              <div className="mt-4 p-4 bg-xtnd-light-100 dark:bg-xtnd-light-900 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-xtnd-dark dark:text-xtnd-white">
                      Authorize XTND App
                    </h4>
                    <p className="text-xs text-gray-600 dark:text-xtnd-light-50 mt-1">
                      Click to automatically connect to QuickBooks Online and exchange for tokens
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="primary"
                    size="md"
                    onClick={handleAuthorizeApp}
                    loading={oauthUrlMutation.isPending}
                    disabled={
                      oauthUrlMutation.isPending ||
                      !formData.clientId.trim() ||
                      !formData.clientSecret.trim()
                    }
                    leftIcon={<Icon name="HiExternalLink" size="sm" />}
                  >
                    {oauthUrlMutation.isPending ? 'Generating URL...' : 'Authorize App'}
                  </Button>
                </div>
                {qboConfig?.configured && (
                  <div className="text-xs text-green-600 dark:text-green-400 mt-2">
                    ✓ App is already authorized with valid tokens
                  </div>
                )}
              </div>
            </div>
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={isSubmitting} disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </div>
    </Dialog>
  );
};
